import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  ServiceValues,
  configKeyMatrix,
} from '@internal/backstage-plugin-release-common';

const useStyles = makeStyles(theme => ({
  legend: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  // The same stripe the rows carry, so the legend reads as "this mark means
  // this" rather than as a colour of its own.
  swatch: {
    display: 'inline-block',
    width: 3,
    height: '1em',
    borderRadius: 1,
  },
  missing: { background: theme.palette.error.main },
  differs: { background: theme.palette.warning.main },
}));

interface Props {
  values: ServiceValues;
  workload: string;
}

/**
 * What the stripes on the environment-variable rows mean, with counts.
 *
 * Colour alone says only "look here"; this says why. The red mark is a key
 * missing from an environment — the miss the tab exists to catch. The amber
 * mark is a value that differs between environments for a reason other than
 * the environment's own name. Neither is an error in itself, which is why they
 * are not called warning and critical.
 */
export const DriftLegend = ({ values, workload }: Props) => {
  const classes = useStyles();
  const rows = configKeyMatrix(values, workload);
  const missing = rows.filter(row => row.missingIn.length > 0).length;
  const differs = rows.filter(row => row.missingIn.length === 0 && row.differsBeyondNaming).length;
  const keys = `${rows.length} key${rows.length === 1 ? '' : 's'}`;

  return (
    <Box component="span" className={classes.legend}>
      <Typography variant="body2" color="textSecondary" component="span">
        {keys}
      </Typography>
      {missing === 0 && differs === 0 ? (
        <Typography variant="body2" color="textSecondary" component="span">
          · same across environments
        </Typography>
      ) : (
        <>
          {missing > 0 && (
            <Typography variant="body2" color="textSecondary" component="span" className={classes.item}>
              · <span className={`${classes.swatch} ${classes.missing}`} aria-hidden />
              {missing} missing in an environment
            </Typography>
          )}
          {differs > 0 && (
            <Typography variant="body2" color="textSecondary" component="span" className={classes.item}>
              · <span className={`${classes.swatch} ${classes.differs}`} aria-hidden />
              {differs} differ{differs === 1 ? 's' : ''} beyond environment naming
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
